import { useCallback, useEffect, useRef, useState } from 'react';

function compareObjectEquality(x: unknown, y: unknown) {
  return x === y;
}

/**
 * A function that compares a string with the "value" key of a given option.
 *
 * This is useful for comparing inputs coming from <ExinMuiRadioGroup> components -- which are always strings.
 */
export function compareSelectInput<P extends { value: string }>(x: null | string | P, y: P) {
  if (typeof x === 'string') {
    return y.value === x;
  }
  return x === y;
}

/**
 * A function that compares a string or object with the "value" key of an option or the option itself.
 *
 * This is useful for comparing inputs coming from <ExinSelect> components -- which are strings for
 * their initial value (if based on URL query string) or objects when selected via UI.
 */
export function compareRadioInput<P extends { value: string }>(x: null | string, y: P) {
  return y.value === x;
}

/**
 * This hook handles the case where, from a list of (database) objects max. 1 object is
 * selected via the "value" primary key.
 *
 * Optionally, the hook will also call an "onChange" prop with the selected object. This callback
 * is only called (if it is provided and) if indeed an object is selected -- it cannot be used to
 * "unset" a selection.
 *
 * Hook returns a tupel of the selected object and a function to select a different object by its
 * "value" primary key.
 */
export default function useSelectedObjectState<K, P = K>(
  objects: Array<P> | null,
  initialValue: K | null,
  {
    onChange,
    comparator,
  }: {
    onChange?: (object: P) => void;
    comparator?: (input: K | null, object: P) => boolean;
  } = {}
): [P | null, (value: K) => void] {
  const [selectedObject, setSelectedObject] = useState<P | null>(
    (objects || []).find((o) => (comparator || compareObjectEquality)(initialValue, o)) || null
  );

  const setSelectedObjectByValue = useCallback(
    (value: K | null) => {
      if (!objects) {
        return;
      }
      const object = objects.find((o) => (comparator || compareObjectEquality)(value, o));
      const isChanged = object !== selectedObject;
      if (!isChanged) {
        return;
      }
      setSelectedObject(object || null);
      if (object && typeof onChange === 'function') {
        onChange(object);
      }
    },
    [objects, selectedObject, onChange, comparator]
  );

  const lastObjectList = useRef<Array<P> | null>(null);
  useEffect(() => {
    if (Array.isArray(objects) && !lastObjectList.current) {
      setSelectedObjectByValue(initialValue);
    }
    lastObjectList.current = objects;
  }, [objects, initialValue, setSelectedObjectByValue]);

  return [selectedObject, setSelectedObjectByValue];
}
